import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "@/components/ui/icons";

import {
  DIALOG_CLOSE_TEXT_CLEARANCE,
  TEXT_DESCRIPTION,
} from "@/lib/surface-styles";
import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

function DialogOverlay({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Overlay>>;
}) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-overlay/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Standard dialog widths.
 *
 * - `sm` — confirm prompts, single-question modals.
 * - `md` — default. Most dialogs (composer, simple settings).
 * - `lg` — settings hubs, admin tabs, anything with a sidebar layout.
 *
 * Pair this with `max-h-[85vh]` (already baked into the base) to keep the
 * dialog within the viewport and let inner content scroll. Avoid passing
 * an ad-hoc `className="max-w-…"` override unless the dialog genuinely
 * needs an unusual size — pick the closest standard width instead.
 */
export type DialogSize = "sm" | "md" | "lg";

// Below the `sm:` breakpoint (< 640px) the dialog needs an explicit
// inset cap so it doesn't sit flush against the viewport edges on a
// phone. Above `sm:` the original max-w-{sm,md,lg} caps apply
// unchanged — desktop is byte-identical.
const DIALOG_SIZE_CLASSES: Record<DialogSize, string> = {
  sm: "max-w-[calc(100%-2rem)] sm:max-w-sm",
  md: "max-w-[calc(100%-2rem)] sm:max-w-lg",
  lg: "max-w-[calc(100%-2rem)] sm:max-w-2xl",
};

function DialogContent({
  className,
  children,
  ref,
  hideCloseButton = false,
  size = "md",
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Content>>;
  hideCloseButton?: boolean;
  size?: DialogSize;
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-h-[85vh] translate-x-[-50%] translate-y-[-50%] gap-4 border border-stroke-card bg-modal-opaque p-6 shadow-lg duration-150 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-lg",
          DIALOG_SIZE_CLASSES[size],
          className,
        )}
        {...props}
      >
        {children}
        {hideCloseButton ? null : (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // Reserve space for DialogPrimitive.Close at `right-4 top-4`.
      // Trailing header actions use SettingsDialogHeader `actions` +
      // DIALOG_CLOSE_ACTIONS_CLEARANCE instead of relying on this alone.
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        DIALOG_CLOSE_TEXT_CLEARANCE,
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Title>>;
}) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Description>>;
}) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(TEXT_DESCRIPTION, className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
